import { useCallback, useRef, useTransition, type FC } from 'react';
import { graphql, useFragment, useRefetchableFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { SbSharedMarketTrendsPriceStats } from './SbSharedMarketTrendsPriceStats.tsx';
import { SbSharedMarketTrendsTopSales } from './SbSharedMarketTrendsTopSales.tsx';
import { FILTER_NAME_CREATOR } from '../../constants/sbConstants.ts';
import { getTopicDescription } from './languageHelpers.ts';
import { getAppliedFilterDisplayName } from '../helpers/appliedFilters/getAppliedFilterDisplayName.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { trackEvent, eventNameConstants } from 'dibs-tracking';
import { pageTypeConstants } from '../../constants/pageTypeConstants.ts';
import { removeAllQueryParameters } from '../../utils/uriUtils.ts';

import dibsCss from 'dibs-css';
import cn from 'classnames';

import { type SbSharedMarketTrends_viewer$key } from './__generated__/SbSharedMarketTrends_viewer.graphql.ts';
import { type SbSharedMarketTrends_itemSearch$key } from './__generated__/SbSharedMarketTrends_itemSearch.graphql.ts';

const viewerFragment = graphql`
    fragment SbSharedMarketTrends_viewer on Viewer
    @refetchable(queryName: "SbSharedMarketTrendsQuery") {
        creatorMarketTrends(uriRef: $marketTrendsUrl, first: 5) {
            ...SbSharedMarketTrendsPriceStats_creatorMarketTrends
            ...SbSharedMarketTrendsTopSales_creatorMarketTrends
            priceStats {
                __typename
            }
            edges {
                __typename
            }
            topicDescription {
                categoryName
                hasArticle
            }
        }
    }
`;

const itemSearchFragment = graphql`
    fragment SbSharedMarketTrends_itemSearch on ItemSearchQueryConnection {
        ...getAppliedFilterDisplayName_itemSearch
        pageType
    }
`;

type Props = {
    viewer: SbSharedMarketTrends_viewer$key;
    itemSearch: SbSharedMarketTrends_itemSearch$key;
};

export const SbSharedMarketTrends: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
}) => {
    const [viewer, refetch] = useRefetchableFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const [isPending, startTransition] = useTransition();
    const { isDesktop } = useServerVarsContext();
    const { creatorMarketTrends } = viewer || {};
    const { edges, priceStats, topicDescription } = creatorMarketTrends || {};
    const { uriRef } = useSbSelector(state => state.relayVariables.variables);
    const marketTrendsRef = useRef<HTMLDivElement>(null);
    const marketTrendsUrl = removeAllQueryParameters(uriRef);

    const doRefetch = useCallback(() => {
        startTransition(() => {
            refetch({ marketTrendsUrl });
        });
    }, [refetch, marketTrendsUrl]);

    if (itemSearch.pageType !== pageTypeConstants.CREATOR) {
        return null;
    }

    const creatorDisplayName = getAppliedFilterDisplayName(itemSearch, FILTER_NAME_CREATOR) ?? '';
    const { categoryName, hasArticle } = topicDescription || {};
    const topic = getTopicDescription({ creatorDisplayName, categoryName, hasArticle });

    return (
        <>
            <VisibilityTracker
                watchAfterFirstVisible
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        doRefetch();
                    }
                }}
                observerOptions={{ rootMargin: `800px 0px` }}
            />
            <VisibilityTracker
                elementRef={marketTrendsRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        // Track actual user view of market trends event
                        trackEvent({
                            eventName: eventNameConstants.EVENT_BROWSE_INTERACTION,
                            interaction_type: 'market trends - view',
                        });
                    }
                }}
            />

            {isPending ? (
                <div
                    className={cn(
                        dibsCss.flex,
                        dibsCss.justifyCenter,
                        dibsCss.itemsCenter,
                        dibsCss.wFull,
                        dibsCss.pyLarge
                    )}
                >
                    <Spinner black />
                </div>
            ) : (
                <>
                    {creatorMarketTrends && (priceStats || edges?.length) && (
                        <article
                            ref={marketTrendsRef}
                            className={cn(
                                dibsCss.textNoir,
                                dibsCss.borderDolphin,
                                ...(isDesktop
                                    ? [
                                          dibsCss.myLarger,
                                          dibsCss.pLarger,
                                          dibsCss.border,
                                          dibsCss.borderSolid,
                                      ]
                                    : [
                                          dibsCss.myLarge,
                                          dibsCss.pyMedium,
                                          dibsCss.borderY,
                                          dibsCss.borderYSolid,
                                      ])
                            )}
                        >
                            <HeadingLevel>
                                {Heading => (
                                    <>
                                        <Heading
                                            className={cn(
                                                dibsCss.sassyFontHeaderLargeNormal,
                                                dibsCss.mt0,
                                                isDesktop ? dibsCss.mbSmall : dibsCss.mbMedium
                                            )}
                                        >
                                            <FormattedMessage
                                                id="abf.SbSharedMarketTrends.header"
                                                defaultMessage="Market Trends"
                                            />
                                        </Heading>
                                        <SbSharedMarketTrendsPriceStats
                                            creatorMarketTrends={creatorMarketTrends}
                                            topic={topic}
                                        />
                                        <SbSharedMarketTrendsTopSales
                                            creatorMarketTrends={creatorMarketTrends}
                                            topic={topic}
                                        />
                                    </>
                                )}
                            </HeadingLevel>
                        </article>
                    )}
                </>
            )}
        </>
    );
};
